import {Injectable} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    ValidationErrors,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {
    AutomationPolicyConfig,
    AutomationPolicyPresetSpec,
    AutomationPolicySpec,
    ConditionSpec,
    FieldSpec,
    MobileElementSelector
} from '@webmate-web-core/shared/types/browsersession';

export type AutomationPolicyEditorMode = 'web' | 'mobile';
export type AutomationPolicyEditorTargetType = 'selector' | 'label' | 'name' | 'placeholder';
export type AutomationPolicyEditorInputMode = 'nativeSet' | 'type' | 'typeWithDelay';
export type AutomationPolicyEditorSubmitType =
    'none'
    | 'pressEnterOnLastFilled'
    | 'clickSelector'
    | 'submitNearestFormOfLastFilled';

export interface AutomationPolicyEditorFormValue {
    autoclickElements: boolean;
    autoclickElementsSelectors: string;
    autoexecJavascript: boolean;
    autoexecJavascriptDomContainsSelector: string;
    autoexecJavascriptCode: string;
    autofillForm: boolean;
    fields: AutomationPolicyEditorFieldValue[];
    fillFormSubmit: AutomationPolicyEditorSubmitValue;
    autosetCookies: boolean;
    cookies: AutomationPolicyEditorCookieValue[];
    autosetLocalStorage: boolean;
    localStorageItems: AutomationPolicyEditorLocalStorageValue[];
    autolsUrlRegex: string;
    autoclickMobileElement: boolean;
    mobileElementLocator: AutomationPolicyEditorMobileLocatorValue;
}

export type MobileSelectorStrategy = MobileElementSelector['strategy']['type'];

export interface AutomationPolicyEditorMobileLocatorValue {
    strategy: MobileSelectorStrategy;
    value: string;
}

export interface AutomationPolicyEditorFieldValue {
    targetType: AutomationPolicyEditorTargetType;
    targetValue: string;
    value: string;
    inputMode: AutomationPolicyEditorInputMode;
    delayMs: number;
    clearFirst: boolean;
    focus: boolean;
    maskInLogs: boolean;
}

export interface AutomationPolicyEditorSubmitValue {
    type: AutomationPolicyEditorSubmitType;
    selector: string;
    scrollIntoView: boolean;
}

export interface AutomationPolicyEditorCookieValue {
    cookieName: string;
    cookieValue: string;
    secure: boolean;
}

export interface AutomationPolicyEditorLocalStorageValue {
    lsKey: string;
    lsValue: string;
}

export interface AutomationPolicyEditorParseResult {
    initialValue: AutomationPolicyEditorFormValue;
    passthroughPresetSpecs: AutomationPolicyPresetSpec[];
    passthroughPolicySpecs: AutomationPolicySpec[];
}

@Injectable({providedIn: 'root'})
export class AutomationPolicyEditorService {
    constructor(private readonly formBuilder: FormBuilder) {
    }

    createForm(value?: Partial<AutomationPolicyEditorFormValue>): UntypedFormGroup {
        const initialValue = {
            ...this.createEmptyValue(),
            ...(value || {}),
        };

        return this.formBuilder.group({
            autoclickElements: new UntypedFormControl(initialValue.autoclickElements),
            autoclickElementsSelectors: new UntypedFormControl(initialValue.autoclickElementsSelectors),
            autoexecJavascript: new UntypedFormControl(initialValue.autoexecJavascript),
            autoexecJavascriptDomContainsSelector: new UntypedFormControl(initialValue.autoexecJavascriptDomContainsSelector),
            autoexecJavascriptCode: new UntypedFormControl(initialValue.autoexecJavascriptCode),
            autofillForm: new UntypedFormControl(initialValue.autofillForm),
            fields: this.formBuilder.array((initialValue.fields || []).map(field => this.createFieldGroup(field))),
            fillFormSubmit: this.formBuilder.group({
                type: new UntypedFormControl(initialValue.fillFormSubmit.type),
                selector: new UntypedFormControl(initialValue.fillFormSubmit.selector),
                scrollIntoView: new UntypedFormControl(initialValue.fillFormSubmit.scrollIntoView),
            }),
            autosetCookies: new UntypedFormControl(initialValue.autosetCookies),
            cookies: this.formBuilder.array((initialValue.cookies || []).map(cookie => this.createCookieGroup(cookie))),
            autosetLocalStorage: new UntypedFormControl(initialValue.autosetLocalStorage),
            localStorageItems: this.formBuilder.array((initialValue.localStorageItems || []).map(item => this.createLocalStorageItemGroup(item))),
            autolsUrlRegex: new UntypedFormControl(initialValue.autolsUrlRegex),
            autoclickMobileElement: new UntypedFormControl(initialValue.autoclickMobileElement),
            mobileElementLocator: this.createMobileLocatorGroup(initialValue.mobileElementLocator),
        }, {validators: this.mobileClickPolicyValidator});
    }

    createMobileLocatorGroup(locator?: Partial<AutomationPolicyEditorMobileLocatorValue>): UntypedFormGroup {
        const value = {
            strategy: 'AccessibilityId' as MobileSelectorStrategy,
            value: '',
            ...(locator || {}),
        };

        return this.formBuilder.group({
            strategy: new UntypedFormControl(value.strategy),
            value: new UntypedFormControl(value.value),
        });
    }

    createFieldGroup(field?: Partial<AutomationPolicyEditorFieldValue>): UntypedFormGroup {
        const value = {
            ...this.createEmptyFieldValue(),
            ...(field || {}),
        };

        return this.formBuilder.group({
            targetType: new UntypedFormControl(value.targetType),
            targetValue: new UntypedFormControl(value.targetValue, Validators.required),
            value: new UntypedFormControl(value.value, Validators.required),
            inputMode: new UntypedFormControl(value.inputMode),
            delayMs: new UntypedFormControl(value.delayMs),
            clearFirst: new UntypedFormControl(value.clearFirst),
            focus: new UntypedFormControl(value.focus),
            maskInLogs: new UntypedFormControl(value.maskInLogs),
        });
    }

    createCookieGroup(cookie?: Partial<AutomationPolicyEditorCookieValue>): UntypedFormGroup {
        const value = {
            ...this.createEmptyCookieValue(),
            ...(cookie || {}),
        };

        return this.formBuilder.group({
            cookieName: new UntypedFormControl(value.cookieName, Validators.required),
            cookieValue: new UntypedFormControl(value.cookieValue, Validators.required),
            secure: new UntypedFormControl(value.secure),
        });
    }

    createLocalStorageItemGroup(item?: Partial<AutomationPolicyEditorLocalStorageValue>): UntypedFormGroup {
        const value = {
            ...this.createEmptyLocalStorageValue(),
            ...(item || {}),
        };

        return this.formBuilder.group({
            lsKey: new UntypedFormControl(value.lsKey, Validators.required),
            lsValue: new UntypedFormControl(value.lsValue, Validators.required),
        });
    }

    parseConfig(config?: AutomationPolicyConfig | null, mode: AutomationPolicyEditorMode = 'web'): AutomationPolicyEditorParseResult {
        const initialValue = this.createEmptyValue();
        const passthroughPresetSpecs: AutomationPolicyPresetSpec[] = [];
        const passthroughPolicySpecs: AutomationPolicySpec[] = [];

        const presetSpecs = config?.presetSpecs || [];
        const clickPresetSelectors = presetSpecs
            .filter((preset): preset is Extract<AutomationPolicyPresetSpec, { type: 'ClickPolicyPreset' }> => preset.type === 'ClickPolicyPreset')
            .flatMap(preset => preset.selectors || []);

        presetSpecs.forEach(preset => {
            if (mode === 'web' && preset.type === 'ClickPolicyPreset') {
                return;
            }
            if (mode === 'mobile' && preset.type === 'MobileClickPolicyPreset' && !initialValue.autoclickMobileElement) {
                initialValue.autoclickMobileElement = true;
                initialValue.mobileElementLocator = {
                    strategy: preset.selector.strategy.type,
                    value: preset.selector.value,
                };
                return;
            }
            passthroughPresetSpecs.push(preset);
        });

        if (mode === 'web' && clickPresetSelectors.length > 0) {
            initialValue.autoclickElements = true;
            initialValue.autoclickElementsSelectors = clickPresetSelectors.join(', ');
        }

        const editablePolicySpecs = mode === 'web'
            ? (config?.policySpecs || []).filter(spec => !this.hasExplicitApplicability(spec))
            : [];
        const localStorageCandidates = editablePolicySpecs.filter(spec => this.isSupportedLocalStorageSpec(spec));
        const absorbLocalStorageSpecs = this.canAbsorbLocalStorageSpecs(localStorageCandidates);
        const absorbedLocalStorageIds = new Set(localStorageCandidates.filter(() => absorbLocalStorageSpecs).map(spec => spec.name));

        (config?.policySpecs || []).forEach(spec => {
            if (mode === 'mobile') {
                passthroughPolicySpecs.push(spec);
                return;
            }
            if (this.hasExplicitApplicability(spec)) {
                passthroughPolicySpecs.push(spec);
                return;
            }

            if (this.isSupportedFillFormSpec(spec) && !initialValue.autofillForm) {
                initialValue.autofillForm = true;
                initialValue.fields = this.parseFields(spec);
                initialValue.fillFormSubmit = this.parseSubmit(spec);
                return;
            }

            if (this.isSupportedSetCookiesSpec(spec) && !initialValue.autosetCookies) {
                initialValue.autosetCookies = true;
                initialValue.cookies = this.parseCookies(spec);
                return;
            }

            if (this.isSupportedExecuteJsSpec(spec) && !initialValue.autoexecJavascript) {
                initialValue.autoexecJavascript = true;
                initialValue.autoexecJavascriptCode = spec.remediation.type === 'ExecuteJavaScript' ? spec.remediation.script : '';
                initialValue.autoexecJavascriptDomContainsSelector = this.parseDomContainsSelector(spec.conditions);
                return;
            }

            if (absorbLocalStorageSpecs && absorbedLocalStorageIds.has(spec.name)) {
                initialValue.autosetLocalStorage = true;
                initialValue.localStorageItems.push(this.parseLocalStorageItem(spec));
                initialValue.autolsUrlRegex = this.parseLocalStorageRegex(spec.conditions);
                return;
            }

            passthroughPolicySpecs.push(spec);
        });

        return {
            initialValue,
            passthroughPresetSpecs,
            passthroughPolicySpecs,
        };
    }

    serializeForm(
        form: UntypedFormGroup,
        passthroughPresetSpecs: AutomationPolicyPresetSpec[] = [],
        passthroughPolicySpecs: AutomationPolicySpec[] = [],
        mode: AutomationPolicyEditorMode = 'web'
    ): AutomationPolicyConfig | undefined {
        const value = form.getRawValue() as AutomationPolicyEditorFormValue;

        const presetSpecs: AutomationPolicyPresetSpec[] = [...passthroughPresetSpecs];
        const policySpecs: AutomationPolicySpec[] = [...passthroughPolicySpecs];

        if (mode === 'web' && value.autoclickElements) {
            const selectors = (value.autoclickElementsSelectors || '')
                .split(',')
                .map(selector => selector.trim())
                .filter(selector => selector.length > 0);

            if (selectors.length > 0) {
                presetSpecs.unshift({
                    type: 'ClickPolicyPreset',
                    selectors,
                });
            }
        }

        const mobilePresetSpecs: AutomationPolicyPresetSpec[] = [];
        if (mode === 'mobile' && value.autoclickMobileElement) {
            const selector = {
                strategy: {type: value.mobileElementLocator.strategy},
                value: (value.mobileElementLocator.value || '').trim(),
            };
            if (selector.value.length > 0) {
                mobilePresetSpecs.push({type: 'MobileClickPolicyPreset', selector});
            }
        }

        presetSpecs.unshift(...mobilePresetSpecs);

        const setCookiesSpec = mode === 'web' ? this.buildSetCookiesPolicySpec(value) : null;
        if (setCookiesSpec) {
            policySpecs.unshift(setCookiesSpec);
        }

        const fillFormSpec = mode === 'web' ? this.buildFillFormPolicySpec(value) : null;
        if (fillFormSpec) {
            policySpecs.unshift(fillFormSpec);
        }

        const executeJsSpec = mode === 'web' ? this.buildExecuteJsPolicySpec(value) : null;
        if (executeJsSpec) {
            policySpecs.unshift(executeJsSpec);
        }

        const localStorageSpecs = mode === 'web' ? this.buildSetLocalStoragePolicySpecs(value) : [];
        if (localStorageSpecs.length > 0) {
            policySpecs.unshift(...localStorageSpecs);
        }

        if (presetSpecs.length === 0 && policySpecs.length === 0) {
            return undefined;
        }

        return {
            presetSpecs,
            policySpecs,
        };
    }

    private createEmptyValue(): AutomationPolicyEditorFormValue {
        return {
            autoclickElements: false,
            autoclickElementsSelectors: '',
            autoexecJavascript: false,
            autoexecJavascriptDomContainsSelector: '',
            autoexecJavascriptCode: '',
            autofillForm: false,
            fields: [],
            fillFormSubmit: {
                type: 'none',
                selector: '',
                scrollIntoView: true,
            },
            autosetCookies: false,
            cookies: [],
            autosetLocalStorage: false,
            localStorageItems: [],
            autolsUrlRegex: '',
            autoclickMobileElement: false,
            mobileElementLocator: {
                strategy: 'AccessibilityId',
                value: '',
            },
        };
    }

    private readonly mobileClickPolicyValidator = (control: AbstractControl): ValidationErrors | null => {
        if (!control.get('autoclickMobileElement')?.value) {
            return null;
        }

        const locatorValue = control.get('mobileElementLocator.value')?.value;
        return typeof locatorValue === 'string' && locatorValue.trim().length > 0
            ? null
            : {mobileElementLocatorRequired: true};
    };

    private createEmptyFieldValue(): AutomationPolicyEditorFieldValue {
        return {
            targetType: 'selector',
            targetValue: '',
            value: '',
            inputMode: 'nativeSet',
            delayMs: 50,
            clearFirst: true,
            focus: true,
            maskInLogs: false,
        };
    }

    private createEmptyCookieValue(): AutomationPolicyEditorCookieValue {
        return {
            cookieName: '',
            cookieValue: '',
            secure: true,
        };
    }

    private createEmptyLocalStorageValue(): AutomationPolicyEditorLocalStorageValue {
        return {
            lsKey: '',
            lsValue: '',
        };
    }

    private buildFillFormPolicySpec(value: AutomationPolicyEditorFormValue): AutomationPolicySpec | null {
        if (!value.autofillForm || value.fields.length === 0) {
            return null;
        }

        const fields = value.fields.map(field => this.buildFieldSpec(field));
        const absentSelectors = value.fields
            .map(field => this.buildAbsentSelector(field))
            .filter((selector): selector is string => typeof selector === 'string' && selector.length > 0);

        return {
            name: 'FillForm',
            invariant: {type: 'AllElementsAreAbsent', selectors: absentSelectors},
            remediation: {
                type: 'FillFields',
                fields,
                submit: this.buildSubmitAction(value.fillFormSubmit),
                scrollIntoView: value.fillFormSubmit.scrollIntoView !== false,
            },
            triggers: [{type: 'AfterNavigation'}],
            scope: {type: 'OncePerPage'},
        };
    }

    private buildSetCookiesPolicySpec(value: AutomationPolicyEditorFormValue): AutomationPolicySpec | null {
        if (!value.autosetCookies || value.cookies.length === 0) {
            return null;
        }

        const cookieItems = value.cookies
            .map(cookie => ({
                name: (cookie.cookieName || '').trim(),
                value: (cookie.cookieValue || '').trim(),
            }))
            .filter(cookie => cookie.name.length > 0);

        if (cookieItems.length === 0) {
            return null;
        }

        return {
            name: 'SetCookies',
            invariant: {
                type: 'AllCookiesMatch',
                cookies: cookieItems.map(({name, value}) => ({name, value})),
            },
            remediation: {
                type: 'SetCookies',
                cookieItems: cookieItems.map(({name, value}) => ({name, value})),
            },
            triggers: [{type: 'OnSessionStart'}, {type: 'AfterNavigation'}],
            scope: {type: 'OncePerPage'},
        };
    }

    private buildSetLocalStoragePolicySpecs(value: AutomationPolicyEditorFormValue): AutomationPolicySpec[] {
        if (!value.autosetLocalStorage || value.localStorageItems.length === 0) {
            return [];
        }

        const conditions: ConditionSpec[] = [];
        const pattern = (value.autolsUrlRegex || '').trim();
        if (pattern.length > 0) {
            conditions.push({type: 'UrlMatches', pattern});
        }

        return value.localStorageItems
            .map(item => ({
                key: (item.lsKey || '').trim(),
                value: (item.lsValue || '').trim(),
            }))
            .filter(item => item.key.length > 0)
            .map(item => ({
                name: `SetLocalStorage:${item.key}`,
                invariant: {type: 'LocalStorageEquals', key: item.key, value: item.value},
                remediation: {type: 'SetLocalStorage', key: item.key, value: item.value},
                conditions: conditions.length > 0 ? [...conditions] : undefined,
                triggers: [{type: 'OnSessionStart'}, {type: 'AfterNavigation'}],
                scope: {type: 'OncePerPage'},
            }));
    }

    private buildExecuteJsPolicySpec(value: AutomationPolicyEditorFormValue): AutomationPolicySpec | null {
        if (!value.autoexecJavascript) {
            return null;
        }

        const script = (value.autoexecJavascriptCode || '').trim();
        if (script.length === 0) {
            return null;
        }

        const domContainsSelector = (value.autoexecJavascriptDomContainsSelector || '').trim();
        if (domContainsSelector.length > 0) {
            return {
                name: 'ExecuteJS',
                invariant: {type: 'JavaScriptExecuted'},
                remediation: {type: 'ExecuteJavaScript', script},
                conditions: [{type: 'DomContains', selector: domContainsSelector}],
                triggers: [{type: 'OnDomChange'}],
                scope: {type: 'OncePerPage'},
            };
        }

        return {
            name: 'ExecuteJS',
            invariant: {type: 'JavaScriptExecuted'},
            remediation: {type: 'ExecuteJavaScript', script},
            triggers: [{type: 'AfterNavigation'}],
            scope: {type: 'OncePerPage'},
        };
    }

    private buildFieldSpec(field: AutomationPolicyEditorFieldValue): FieldSpec {
        const targetValue = (field.targetValue || '').trim();
        const target =
            field.targetType === 'selector' ? {type: 'selector' as const, selector: targetValue} :
                field.targetType === 'label' ? {type: 'label' as const, text: targetValue} :
                    field.targetType === 'name' ? {type: 'name' as const, name: targetValue} :
                        {type: 'placeholder' as const, text: targetValue};

        const inputMode =
            field.inputMode === 'nativeSet' ? {type: 'nativeSet' as const} :
                field.inputMode === 'type' ? {type: 'type' as const} :
                    {type: 'typeWithDelay' as const, ms: Math.max(1, Number(field.delayMs) || 1)};

        return {
            target,
            value: field.value,
            clearFirst: !!field.clearFirst,
            focus: !!field.focus,
            inputMode,
            maskInLogs: !!field.maskInLogs,
        };
    }

    private buildAbsentSelector(field: AutomationPolicyEditorFieldValue): string | undefined {
        const targetValue = (field.targetValue || '').trim();
        if (targetValue.length === 0) {
            return undefined;
        }

        if (field.targetType === 'selector') {
            return targetValue;
        }

        if (field.targetType === 'name') {
            return `[name="${this.cssEscape(targetValue)}"]`;
        }

        if (field.targetType === 'placeholder') {
            return `[placeholder="${this.cssEscape(targetValue)}"]`;
        }

        return undefined;
    }

    private buildSubmitAction(value: AutomationPolicyEditorSubmitValue) {
        if (value.type === 'pressEnterOnLastFilled') {
            return {type: 'pressEnterOnLastFilled' as const};
        }

        if (value.type === 'clickSelector') {
            return {
                type: 'clickSelector' as const,
                selector: (value.selector || '').trim(),
            };
        }

        if (value.type === 'submitNearestFormOfLastFilled') {
            return {type: 'submitNearestFormOfLastFilled' as const};
        }

        return {type: 'none' as const};
    }

    private parseFields(spec: AutomationPolicySpec): AutomationPolicyEditorFieldValue[] {
        if (spec.remediation.type !== 'FillFields') {
            return [];
        }

        return (spec.remediation.fields || []).map(field => {
            const target = field.target;
            const inputMode = field.inputMode || {type: 'nativeSet' as const};

            return {
                targetType: target.type,
                targetValue: target.type === 'selector' ? target.selector :
                    target.type === 'name' ? target.name :
                        target.text,
                value: field.value,
                inputMode: inputMode.type,
                delayMs: inputMode.type === 'typeWithDelay' ? inputMode.ms : 50,
                clearFirst: field.clearFirst !== false,
                focus: field.focus !== false,
                maskInLogs: !!field.maskInLogs,
            };
        });
    }

    private parseSubmit(spec: AutomationPolicySpec): AutomationPolicyEditorSubmitValue {
        if (spec.remediation.type !== 'FillFields') {
            return {
                type: 'none',
                selector: '',
                scrollIntoView: true,
            };
        }

        const submit = spec.remediation.submit || {type: 'none' as const};
        return {
            type: submit.type,
            selector: submit.type === 'clickSelector' ? submit.selector : '',
            scrollIntoView: spec.remediation.scrollIntoView !== false,
        };
    }

    private parseCookies(spec: AutomationPolicySpec): AutomationPolicyEditorCookieValue[] {
        if (spec.remediation.type !== 'SetCookies') {
            return [];
        }

        return (spec.remediation.cookieItems || []).map(cookie => ({
            cookieName: cookie.name,
            cookieValue: cookie.value,
            secure: true,
        }));
    }

    private parseLocalStorageItem(spec: AutomationPolicySpec): AutomationPolicyEditorLocalStorageValue {
        if (spec.remediation.type !== 'SetLocalStorage') {
            return this.createEmptyLocalStorageValue();
        }

        return {
            lsKey: spec.remediation.key,
            lsValue: spec.remediation.value,
        };
    }

    private parseLocalStorageRegex(conditions?: ConditionSpec[]): string {
        const matchCondition = (conditions || []).find(
            (condition): condition is Extract<ConditionSpec, { type: 'UrlMatches' }> => condition.type === 'UrlMatches'
        );

        return matchCondition?.pattern || '';
    }

    private parseDomContainsSelector(conditions?: ConditionSpec[]): string {
        const domContainsCondition = (conditions || []).find(
            (condition): condition is Extract<ConditionSpec, { type: 'DomContains' }> => condition.type === 'DomContains'
        );

        return domContainsCondition?.selector || '';
    }

    private canAbsorbLocalStorageSpecs(specs: AutomationPolicySpec[]): boolean {
        if (specs.length === 0) {
            return false;
        }

        const patterns = new Set(specs.map(spec => this.parseLocalStorageRegex(spec.conditions)));
        return patterns.size <= 1;
    }

    private isSupportedFillFormSpec(spec: AutomationPolicySpec): boolean {
        return spec.name === 'FillForm' && spec.remediation.type === 'FillFields';
    }

    private isSupportedSetCookiesSpec(spec: AutomationPolicySpec): boolean {
        return spec.name === 'SetCookies' && spec.remediation.type === 'SetCookies';
    }

    private isSupportedExecuteJsSpec(spec: AutomationPolicySpec): boolean {
        return spec.name === 'ExecuteJS' && spec.remediation.type === 'ExecuteJavaScript';
    }

    private isSupportedLocalStorageSpec(spec: AutomationPolicySpec): boolean {
        return spec.name.startsWith('SetLocalStorage:') && spec.remediation.type === 'SetLocalStorage';
    }

    private hasExplicitApplicability(spec: AutomationPolicySpec): boolean {
        return spec.applicability !== undefined && spec.applicability !== null;
    }

    private cssEscape(value: string): string {
        const cssEscaper = (globalThis as any)?.CSS?.escape;
        return typeof cssEscaper === 'function'
            ? cssEscaper(value)
            : String(value).replace(/["\\]/g, '\\$&');
    }
}
